import { Navigate, useLocation, useMatches, useParams } from "react-router-dom";

import { useVisiblePages } from "@/deal-room/hooks/useVisiblePages";
import type { Room } from "@/deal-room/schema";
import { PageRenderer } from "@/deal-room/templates/registry";

import type { RoomSwitchState } from "@/deal-room/layout/RoomSwitcher";

import { NotFound } from "./NotFound";

// Page leaves under both the gated /<type>/:slug and open /<type>/preview/:slug
// shells. They read room data from whichever parent loader fired (route id
// "room:<type>" or "preview-room:<type>") so navigation between pages doesn't
// refetch.

function useRoom(): Room {
    const match = useMatches().find(
        (m) => typeof m.id === "string" && (m.id.startsWith("room:") || m.id.startsWith("preview-room:")),
    );
    const room = match?.data as Room | undefined;
    if (!room) throw new Error("Room data not present in route hierarchy");
    return room;
}

// Index route: first visible page of the room (e.g. /deal-room/<slug>). Hidden
// pages (e.g. Onboarding before the deal hits Contracting) are skipped here
// so the supplier always lands on a page they're allowed to see.
export function RoomFirstPage() {
    const room = useRoom();
    const { pages } = useVisiblePages(room);
    const firstPage = pages[0];
    if (!firstPage) return <NotFound />;
    return <PageRenderer page={firstPage} room={room} />;
}

const AGREEMENTS_SLUGS = ["agreements", "contracts"];

// /deal-room/<slug>/<page> deep link. Hidden pages 404 for everyone except Bison
// staff - SMs can still preview the Onboarding page before it goes live.
export function RoomPage() {
    const room = useRoom();
    const { pages } = useVisiblePages(room);
    const { page: pageSlug } = useParams<{ page: string }>();
    const location = useLocation();
    const page = pages.find((p) => p.slug === pageSlug);
    if (!page) {
        // The Agreements page is slug "agreements" since 2026-10-26 (Bill);
        // older rooms store "contracts". Either URL lands on the room's own page.
        const agreements = AGREEMENTS_SLUGS.includes(pageSlug ?? "")
            ? pages.find((p) => p.template === "contracts")
            : undefined;
        if (agreements) return <Navigate to={`../${agreements.slug}${location.search}${location.hash}`} replace />;
        // Arrived from the staff room switcher (Elijah, 2026-11-02): this room
        // has no page with that slug, so open its page of the same template,
        // else its Home. A typed or shared bad URL still gets NotFound.
        const from = (location.state as RoomSwitchState | null)?.switchedFromTemplate;
        if (from) {
            const same = pages.find((p) => p.template === from);
            return <Navigate to={same ? `../${same.slug}` : ".."} replace />;
        }
        return <NotFound />;
    }
    return <PageRenderer page={page} room={room} />;
}
